import { useEffect, useId, useRef } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { publicConfig } from 'config'
import {
  BADGE_LOOKS,
  foundingMemberNo,
  memberCardEmptyLine,
  memberCardGearMeta,
  memberCardRank,
  memberHref,
  safeImageUrl,
  sentCount,
  tierLabel,
  tierProgress,
  tileLetters,
} from 'lib/community/model'
import { toNextTierLine } from 'lib/community/hub'
import type { CommunityAuthor, CommunityMemberCardResponse } from 'lib/community/types'
import { DisclosureGroup, FloatingPanel, POPOVER_ZONE_ATTR, useDisclosureGroup } from './Popover'
import MemberName from './MemberName'
import TierAvatar from './TierAvatar'
import { memberCardPrefetcher, useMemberCard, type MemberCardState } from './useMemberCard'
import styles from './MemberCard.module.css'

/** A signed-in author — the only kind with a public page, and so with a card. */
export type MemberAuthor = CommunityAuthor & { member: NonNullable<CommunityAuthor['member']> }

export function isMemberAuthor(author: CommunityAuthor): author is MemberAuthor {
  return author.member !== null
}

/** Hover intent before a card opens: a pointer passing over a feed opens nothing. */
export const MEMBER_CARD_OPEN_DELAY_MS = 250
/** The pointer has rested this long on the trigger: the card's read starts, so by the time the
 *  card opens the answer is in flight or in — a sweep down a feed (no rest) still asks nothing.
 *  The fallback for a handle the page's idle prefetch (`memberCardPrefetcher`) did not reach; a
 *  no-op on a cached one. */
export const MEMBER_CARD_WARM_DELAY_MS = 100

/** Up to this many earned badge names as chips; the rest fold into "+N" (Amendment B.3). */
const CARD_BADGE_CHIPS = 4

/**
 * Every author line on a page shares one group: one member card open at a time, across every
 * list on the page (feed, product hub, thread). Module-level because the author lines have no
 * common parent short of each page; a context would need a provider in four places for state
 * that is, by design, page-wide.
 */
export const memberCardGroup = new DisclosureGroup()

/** Everything the card route adds to the instant part. */
const MemberCardFacts: React.VFC<{ data: CommunityMemberCardResponse }> = ({ data }) => {
  const reviews = sentCount(data.counts?.reviews) ?? 0
  const next = data.next_tier
  const readers = sentCount(data.readers_total)
  const extraBadges = data.badges.length - CARD_BADGE_CHIPS
  return (
    <>
      {reviews > 0 ? (
        <span className={styles.block}>
          <span className={styles.cols}>
            <span className={styles.col}>
              <span className={clsx(styles.figure, styles.accent)}>
                {readers === null ? '—' : readers.toLocaleString('en-US')}
              </span>
              <span className={styles.colLabel}>{'readers'}</span>
            </span>
            <span className={styles.col}>
              <span className={styles.figure}>{reviews.toLocaleString('en-US')}</span>
              <span className={styles.colLabel}>{reviews === 1 ? 'review' : 'reviews'}</span>
            </span>
            <span className={styles.col}>
              <span className={styles.figure}>{memberCardRank(data)}</span>
              <span className={styles.colLabel}>{'rank'}</span>
            </span>
          </span>
          <span className={styles.progress}>
            {next !== undefined && (
              <span className={styles.track} aria-hidden="true">
                <span
                  className={styles.fill}
                  style={{
                    width: `${tierProgress(data.points, next ? next.points_to_go : null)}%`,
                  }}
                />
              </span>
            )}
            <span className={styles.progressLine}>
              <span>
                <strong className={styles.strong}>{data.points.toLocaleString('en-US')}</strong>
                {' pts'}
              </span>
              {next !== undefined && <span>{toNextTierLine(next) ?? 'Top tier'}</span>}
            </span>
          </span>
        </span>
      ) : (
        <span className={clsx(styles.block, styles.empty)}>
          <span className={styles.emptyTitle}>{'No owner reviews yet'}</span>
          <span className={styles.emptyLine}>{memberCardEmptyLine(data)}</span>
        </span>
      )}
      {reviews > 0 && data.gear && data.gear.length > 0 && (
        <span className={styles.section}>
          <span className={styles.eyebrow}>{'Owns and reviewed'}</span>
          {data.gear.map(g => {
            const meta = memberCardGearMeta(g)
            const image = safeImageUrl(g.image_url)
            return (
              <span key={g.name} className={styles.gear}>
                <span className={styles.tile} aria-hidden="true">
                  {image ? (
                    // eslint-disable-next-line @next/next/no-img-element -- a 36 px DealHunt thumb, as the Stats tab's
                    <img src={image} alt="" loading="lazy" />
                  ) : (
                    tileLetters({ name: g.name, category: { name: g.category } })
                  )}
                </span>
                <span className={styles.gearText}>
                  <span className={styles.gearName}>{g.name}</span>
                  <span className={styles.gearMeta}>
                    {meta}
                    {meta && g.buy_again !== null && ' · '}
                    {g.buy_again !== null && (
                      <span className={g.buy_again ? styles.buyYes : styles.buyNo}>
                        {g.buy_again ? 'Would buy again' : 'Wouldn’t buy again'}
                      </span>
                    )}
                  </span>
                </span>
              </span>
            )
          })}
        </span>
      )}
      {data.badges_total > 0 && (
        <span className={styles.section}>
          <span
            className={styles.eyebrow}
          >{`Badges · ${data.badges.length} of ${data.badges_total}`}</span>
          {data.badges.length > 0 && (
            <span className={styles.chips}>
              {data.badges.slice(0, CARD_BADGE_CHIPS).map(b => (
                <span key={b.id} className={styles.chip}>
                  {BADGE_LOOKS[b.id].name}
                </span>
              ))}
              {extraBadges > 0 && <span className={styles.chip}>{`+${extraBadges}`}</span>}
            </span>
          )}
        </span>
      )}
    </>
  )
}

/**
 * The card's body (Amendment B.3, design boards 3a–3c). Paints at once from the feed's author
 * fields (avatar, Name @handle, tier pill, Early Owner / Founding Member pills); the rest fills
 * when the card route answers: a grey block of public numbers (readers — Σ of the member's
 * reviews' readers, public since the Stats tab — reviews, all-time rank, the tier bar), "Owns and
 * reviewed" (the ≤ 2 most-read reviews, each with its product thumbnail — category letters when
 * DealHunt sent none), or "No owner reviews yet" for a member without one; then
 * the earned badges by name. No VerifiedCheck inside: the author line beside the trigger already
 * wears it, and its button would be a focus stop and a second popover inside this one. Loading is
 * one skeleton line; a missing member or a failed read keeps the instant part and drops the rest
 * — a hover card never shows an error. A pre-v2 card answer (cached without `next_tier` /
 * `readers_total` / `gear`) draws "—" / no bar / no gear block. Phrasing content only: the card
 * sits inside an author line's `<p>`, so every element is a span.
 */
export const MemberCardBody: React.VFC<{
  author: MemberAuthor
  state: MemberCardState
  data: CommunityMemberCardResponse | null
  href: string
}> = ({ author, state, data, href }) => {
  const tier = data?.tier ?? author.member.tier
  const early = data ? data.founding_number : author.member.founding_number
  const foundingMember = data?.founding_member_number ?? null
  return (
    <>
      <span className={styles.head}>
        <TierAvatar
          handle={author.handle}
          tier={tier}
          size="md"
          src={author.avatar_url}
          name={author.display_name}
        />
        <span className={styles.who}>
          <span className={styles.handle}>
            <MemberName name={author.display_name} handle={author.handle} />
          </span>
          <span className={styles.pills}>
            <span className={styles.tierPill}>{tierLabel(tier)}</span>
            {typeof early === 'number' && (
              <span className={styles.pill}>{`Early Owner #${early.toLocaleString('en-US')}`}</span>
            )}
            {typeof foundingMember === 'number' && (
              <span className={styles.pill}>{`Founding Member ${foundingMemberNo(
                foundingMember
              )}`}</span>
            )}
          </span>
        </span>
      </span>
      {(state === 'idle' || state === 'loading') && (
        <span className={styles.skeleton} aria-hidden="true" />
      )}
      {data && <MemberCardFacts data={data} />}
      {/* The handle link already leads here for keyboard users: this one is for the pointer,
          so the card adds no tab stop to every row. */}
      <Link href={href} prefetch={false} className={styles.profile} tabIndex={-1}>
        {'View profile →'}
      </Link>
    </>
  )
}

/**
 * A signed-in author's avatar + handle link, as the trigger of their hover card: hover intent
 * (250 ms; the read starts at 100 ms of rest, unless the page's idle prefetch already read it) or
 * keyboard focus opens it on hover devices; Escape, leaving (150 ms grace, so the
 * card's link is reachable) or a tap elsewhere closes it; a click on the handle still navigates.
 * Touch devices open nothing — the tap goes to the profile. The check (`VerifiedCheck`) is NOT
 * part of the trigger: render it after this, so its tooltip and the card never open together.
 *
 * Nothing renders while closed, so the server HTML carries no card. The open card is a plain
 * description of the handle link (`aria-describedby`), with no role: focus never moves into it.
 * `next/link` directly (as `Linked` renders it, `prefetch={false}`): `Linked` passes neither
 * `aria-describedby` nor `tabIndex` through.
 */
export const MemberCardTrigger: React.VFC<{
  author: MemberAuthor
  /** The handle link's class (the list's own author style). */
  linkClassName?: string
  /** The shown name without its @handle (MemberName): the feed row's byline. */
  nameOnly?: boolean
}> = ({ author, linkClassName, nameOnly = false }) => {
  const id = useId()
  const cardId = `member-card-${id.replace(/:/g, '')}`
  const anchor = useRef<HTMLSpanElement>(null)
  const href = memberHref(author.handle, publicConfig('useTrailingSlash'))
  const card = useMemberCard(author.handle)
  const { request, warm } = card
  const { openId, triggerProps } = useDisclosureGroup(memberCardGroup)
  const open = openId === cardId

  useEffect(() => () => memberCardGroup.closeIf(cardId), [cardId])
  // The page's idle prefetch (Amendment C.3) watches this trigger; nothing is read here.
  useEffect(() => {
    const el = anchor.current
    return el ? memberCardPrefetcher.register(el, author.handle) : undefined
  }, [author.handle])

  return (
    <span
      ref={anchor}
      className={styles.trigger}
      {...{ [POPOVER_ZONE_ATTR]: '' }}
      {...triggerProps(cardId, {
        openDelayMs: MEMBER_CARD_OPEN_DELAY_MS,
        warmDelayMs: MEMBER_CARD_WARM_DELAY_MS,
        onWarm: warm,
        interactive: true,
        clickPins: false,
        onOpen: request,
      })}
    >
      <TierAvatar
        handle={author.handle}
        tier={author.member.tier}
        size="xs"
        src={author.avatar_url}
        name={author.display_name}
      />
      <Link
        href={href}
        prefetch={false}
        className={linkClassName}
        aria-describedby={open ? cardId : undefined}
      >
        <MemberName name={author.display_name} handle={author.handle} nameOnly={nameOnly} />
      </Link>
      {open && (
        <FloatingPanel id={cardId} anchor={anchor} dialog={false} as="span" className={styles.card}>
          <MemberCardBody author={author} state={card.state} data={card.data} href={href} />
        </FloatingPanel>
      )}
    </span>
  )
}
